import { memo, useCallback, forwardRef, useImperativeHandle, useRef, useState } from 'react';

import { useSelector } from 'react-redux';
import { Modal, IModal } from 'shared';
import { Button, Typography } from 'shared/Houston';
import { selectMediaQuery } from 'store/selectors';

import { ContainerButton, ContainerText } from './styles';

interface IProps {}

interface IShowAlertProps {
  title: string;
  text: string;
  confirmActionTextButton?: string;
  cancelActionTextButton?: string;
  hideCancelButton?: boolean;
  onConfirm?: (...attr: any[]) => void | Promise<void>;
  onCancel?: (...attr: any[]) => void | null;
}

export interface IAlertDialog {
  showAlertDialog: (options: IShowAlertProps) => void;
}

const AlertDialog = forwardRef<IAlertDialog, IProps>((_, ref) => {
  const [alertOptions, setAlertOptions] = useState<IShowAlertProps>();
  const mediaQuery = useSelector(selectMediaQuery);

  const refModal = useRef<IModal>(null);

  const showAlert = useCallback(
    (options: IShowAlertProps) => {
      setAlertOptions(options);
      refModal.current?.openModal();
    },
    [refModal]
  );

  const refAlertDialog = useRef<IAlertDialog>({
    showAlertDialog: showAlert
  });

  useImperativeHandle(
    ref,
    (): IAlertDialog => ({
      showAlertDialog: refAlertDialog.current?.showAlertDialog
    })
  );

  const handleApplyActionClick = useCallback(() => {
    refModal.current?.closeModal();

    if (alertOptions?.onConfirm) {
      alertOptions?.onConfirm();
    }
  }, [refModal, alertOptions]);

  const handleCancelActionClick = useCallback(() => {
    refModal.current?.closeModal();
    if (alertOptions?.onCancel) {
      alertOptions?.onCancel();
    }
  }, [refModal, alertOptions]);

  return (
    <Modal
      small={!mediaQuery.md}
      id='alert-dialog-modal'
      title={
        <Typography size='xxs' weight='semibold'>
          {alertOptions?.title || 'Atenção'}
        </Typography>
      }
      ref={refModal}
      footer={
        <ContainerButton>
          {!alertOptions?.hideCancelButton && (
            <Button variant='text' onClick={handleCancelActionClick}>
              {alertOptions?.cancelActionTextButton || 'Cancelar'}
            </Button>
          )}
          <Button onClick={handleApplyActionClick}>{alertOptions?.confirmActionTextButton || 'Confirmar'}</Button>
        </ContainerButton>
      }
    >
      <ContainerText>
        <Typography size='xs'>{alertOptions?.text}</Typography>
      </ContainerText>
    </Modal>
  );
});

export default memo(AlertDialog);
